import { useEffect, useMemo, useState } from 'react';

import axios from 'axios';

import {
  CREDIT_ACCOUNT_SETTINGS_URL,
  MERCHANT_SETTINGS_URL,
} from '../services/AutomatedAccountReceivablesService';
import { hasCreditAccountId } from '../utils/creditAccountId';

export function useAutoARSettings(customerCreditAccountID: string) {
  const [accountStatus, setAccountStatus] = useState<boolean>(false);
  const [loadingAccountStatus, setLoadingAccountStatus] = useState(true);
  const [accountError, setAccountError] = useState<boolean>(false);

  const [customerStatus, setCustomerStatus] = useState<boolean | null>(null);
  const [loadingCustomerStatus, setLoadingCustomerStatus] = useState(true);
  const [customerError, setCustomerError] = useState<boolean>(false);

  const loading = useMemo(
    () => loadingAccountStatus || loadingCustomerStatus,
    [loadingAccountStatus, loadingCustomerStatus]
  );

  const error = useMemo(() => accountError || customerError, [accountError, customerError]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data, status } = await axios.get(MERCHANT_SETTINGS_URL);
        if (status === 200) {
          if (data.autoARSettings.enabledByDefault) {
            setAccountStatus(true);
            return;
          }

          setAccountStatus(false);
        }
      } catch (error) {
        setAccountError(true);
      } finally {
        setLoadingAccountStatus(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    if (!hasCreditAccountId(customerCreditAccountID)) {
      setCustomerStatus(null);
      setCustomerError(false);
      setLoadingCustomerStatus(false);
      return;
    }

    setCustomerStatus(null);
    setCustomerError(false);
    setLoadingCustomerStatus(true);

    const fetch = async () => {
      try {
        const { data, status } = await axios.get(
          CREDIT_ACCOUNT_SETTINGS_URL(customerCreditAccountID)
        );

        if (status === 200) {
          if (data.autoARSettings.isInherited) {
            setCustomerStatus(null);
            return;
          }

          if (data.autoARSettings.enabled) {
            setCustomerStatus(true);
            return;
          }

          setCustomerStatus(false);
        }
      } catch (error) {
        setCustomerError(true);
      } finally {
        setLoadingCustomerStatus(false);
      }
    };
    fetch();
  }, [customerCreditAccountID]);

  return {
    loading,
    error,
    accountStatus,
    customerStatus,
  };
}
